import { compileFigure } from './compiler.js';
import { fixtureFor } from './fixtures.js';
import { sliceFixture, pageFixture } from './fixtures.js';
import { isolationMatrixFixture } from './fixtures/isolation-matrix.js';
import { concurrencySequenceFixture } from './fixtures/concurrency-sequence.js';
import { memoryLayoutFixture } from './fixtures/memory-layout.js';
import { queryPlanFixture } from './fixtures/query-plan.js';
import { latencyDistributionFixture } from './fixtures/latency-distribution.js';
import { stackFramesFixture } from './fixtures/stack-frames.js';
import { queueViewFixture } from './fixtures/queue-view.js';
import { complexityArgumentFixture } from './fixtures/complexity-argument.js';
import { executionArtifactFixture, defaultExecutionArtifactFocus, stressExecutionArtifactFocus } from './fixtures/execution-artifact.js';
import { btreeFixture } from './fixtures/btree.js';
// import { protocolStateMachineFixture } from './fixtures/protocol-state-machine.js';
import { compileAnnotationLayer, executionEvidenceAnnotations, memoryTeachingAnnotations } from './annotations/index.js';
import { compilePrimitiveGallery } from './primitives/gallery.js';
import { renderGoLessons } from './go-lessons.js';
import { GO_LESSONS, compileScene, figureWidthFor } from './lessons/index.js';
import { btreeLookupProgram, compileProgram, sliceAppendProgram, sliceReallocationProgram, stackCallProgram, transitionVerbs } from './program.js';
import { HANDS } from './hands.js';
import { FOUNDATION_VERSION, HAND_VERSION, primitiveKinds } from './hand-contract.js';
import { compileMotion } from './motion.js';
import { renderSvg } from './svg.js';
import { transitionFor } from './transitions.js';
import { queueOverloadProgram } from './program.js';
import type { ArtifactTheme, FigureSpec, HandId, MotionPlan, ResolvedFigure } from './model.js';
import type { SemanticCheckpoint, TransitionProgram, TransitionVariant } from './transitions.js';

type ExperienceMode = 'catalogue' | 'guided' | 'explore';
type FeedbackTone = 'correct' | 'wrong';

interface Feedback {
  tone: FeedbackTone;
  message: string;
}

interface AppState {
  family: FigureSpec['family'];
  hand: HandId;
  mode: ExperienceMode;
  variant: TransitionVariant;
  width: number;
  stress: boolean;
  focus: string;
  step: number;
  selected: string | undefined;
  feedback: Feedback | undefined;
  playing: boolean;
  showBounds: boolean;
  showAnchors: boolean;
  annotations: boolean;
  artifactTheme: ArtifactTheme;
}

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('Missing #app');
const PACK_VERSION = 'kiln-foundation-pack/4.2.0';

const state: AppState = {
  family: 'slice-header',
  hand: 'kiln-exact',
  mode: 'catalogue',
  variant: 'primary',
  width: 640,
  stress: false,
  focus: 'overview',
  step: 0,
  selected: undefined,
  feedback: undefined,
  playing: false,
  showBounds: false,
  showAnchors: false,
  // The catalogue is a clean specimen sheet by default. Teacher marks are a
  // deliberate layer the viewer can switch on, not permanent decoration.
  annotations: false,
  artifactTheme: new URLSearchParams(window.location.search).get('theme') === 'dark' ? 'dark' : 'light',
};

let resolved: ResolvedFigure;
let motion: MotionPlan | undefined;
let pendingMotionFrom: ResolvedFigure | undefined;
let playTimer: number | undefined;
let lastCompileMs = 0;

function resolveCurrent(): { program: TransitionProgram; checkpoint: SemanticCheckpoint } {
  const program = transitionFor(state.family, state.width, state.variant);
  state.step = Math.min(state.step, program.checkpoints.length - 1);
  const checkpoint = program.checkpoints[state.step]!;
  let spec = state.mode === 'guided'
    ? checkpoint.spec
    : fixtureFor(state.family, state.width, state.stress, state.focus);
  if (state.mode === 'explore' && state.family === 'execution-artifact' && !state.stress) {
    spec = executionArtifactFixture(state.width, false, state.focus === 'overview' ? undefined : state.focus, 'editor', state.artifactTheme, true);
  }
  const start = performance.now();
  const next = applyAnnotations(compileFigure(spec));
  lastCompileMs = performance.now() - start;
  motion = compileMotion(pendingMotionFrom, next);
  pendingMotionFrom = undefined;
  resolved = next;
  if (state.selected && !resolved.hitRegions.some((hit) => hit.semanticId === state.selected)) state.selected = undefined;
  return { program, checkpoint };
}

function render(): void {
  if (state.mode === 'catalogue') {
    renderCatalogue();
    return;
  }
  const { program, checkpoint } = resolveCurrent();
  const selected = state.selected ? describeSelection(resolved, state.selected) : null;
  const focusOptions = focusOptionsFor(state.family);

  root!.innerHTML = `
    <main class="lab-shell">
      <header class="lab-header">
        <div>
          <div class="eyebrow"><span class="live-dot"></span>Kiln figure engine · interaction spike</div>
          <h1>One truth. <span>Now interactive.</span></h1>
          <p>Touch semantic objects to drive a deterministic state transition. Every checkpoint remains inspectable, replayable and renderer-independent.</p>
        </div>
        <div class="header-proof">
          <span class="proof-label">resolved checkpoint</span>
          <strong>${resolved.geometryFingerprint}</strong>
          <small>${lastCompileMs.toFixed(3)} ms · ${resolved.shapes.length} shapes · ${resolved.anchors.length} anchors</small>
        </div>
      </header>

      <section class="lab-grid">
        <aside class="controls panel">
          <div class="panel-title"><span>Compiler input</span><i>semantic only</i></div>

          <label class="control-label">Experience</label>
          <div class="segmented mode-segments" data-control="mode">
            ${button('catalogue', 'Foundation', false)}
            ${button('guided', 'Guided action', state.mode === 'guided')}
            ${button('explore', 'Explore scene', state.mode === 'explore')}
          </div>

          <label class="control-label">Figure family</label>
          <div class="family-grid" data-control="family">
            ${button('slice-header', 'Exploded slice', state.family === 'slice-header')}
            ${button('database-page', 'Sectioned page', state.family === 'database-page')}
            ${button('isolation-matrix', 'Isolation matrix', state.family === 'isolation-matrix')}
            ${button('concurrency-sequence', 'Concurrency trace', state.family === 'concurrency-sequence')}
            ${button('memory-layout', 'Memory layout', state.family === 'memory-layout')}
            ${button('query-plan', 'Query plan', state.family === 'query-plan')}
            ${button('latency-distribution', 'Latency plot', state.family === 'latency-distribution')}
            ${button('stack-frames', 'Stack frames', state.family === 'stack-frames')}
            ${button('complexity-argument', 'Worked argument', state.family === 'complexity-argument')}
            ${button('execution-artifact', 'System artifact', state.family === 'execution-artifact')}
            ${button('btree', 'B-tree lookup', state.family === 'btree')}
          </div>

          ${state.mode === 'guided' && state.family === 'slice-header' ? `
            <label class="control-label">Append path</label>
            <div class="segmented" data-control="variant">
              ${button('primary', 'Spare capacity', state.variant === 'primary')}
              ${button('reallocate', 'Force growth', state.variant === 'reallocate')}
            </div>` : ''}

          <label class="control-label">Visual hand</label>
          <div class="hand-list" data-control="hand">
            ${Object.values(HANDS).map((item) => `<button class="hand-option${item.id === state.hand ? ' active' : ''}" data-value="${item.id}"><span class="hand-swatch swatch-${item.id}"></span><span><b>${item.name}</b><small>${item.description}</small></span></button>`).join('')}
          </div>

          <label class="control-label" for="width">Logical viewport <output>${state.width}px</output></label>
          <input id="width" type="range" min="320" max="720" step="10" value="${state.width}"/>
          <div class="range-labels"><span>phone</span><span>wide stage</span></div>

          ${state.mode === 'explore'
            ? `<label class="toggle-row"><input id="stress" type="checkbox"${state.stress ? ' checked' : ''}/><span><b>Stress the data</b><small>Long labels, more entities, denser layout</small></span></label>`
            : `<div class="mode-note"><span>Guided action</span><p>The lesson supplies exact semantic checkpoints. Switch to Explore scene for free inspection and stress data.</p></div>`}
        </aside>

        <section class="stage-column">
          <div class="stage-meta">
            <div><span class="view-chip">${resolved.view} view · ${state.mode}</span><h2>${state.mode === 'guided' ? program.title : resolved.title}</h2><p>${state.mode === 'guided' ? program.outcome : resolved.subtitle}</p></div>
            ${state.mode === 'explore'
              ? `<div class="state-picker" data-control="focus">${focusOptions.map(([id, label]) => button(id, label, state.focus === id)).join('')}</div>`
              : `<code class="lesson-code">${program.code}</code>`}
          </div>
          <div class="stage-wrap panel${state.feedback ? ` feedback-${state.feedback.tone}` : ''}" style="--stage-ratio:${resolved.viewport.width / resolved.viewport.height}">
            ${renderSvg(resolved, state.hand, {
              selectedSemanticId: state.selected,
              showBounds: state.showBounds,
              showAnchors: state.showAnchors,
              motion,
              selectionTone: state.feedback?.tone ?? 'default',
            })}
          </div>
          ${state.mode === 'guided' ? lessonConsole(program, checkpoint) : ''}
          <div class="provenance"><span>truth source</span>${resolved.provenance}</div>
          ${resolved.warnings.length ? `<div class="warnings">${resolved.warnings.map((warning) => `<span>${warning}</span>`).join('')}</div>` : ''}
        </section>

        <aside class="inspector panel">
          <div class="panel-title"><span>Scene inspector</span><i>${state.selected ? 'selection' : state.mode}</i></div>
          ${selected ? selectionMarkup(selected) : `<div class="empty-inspector"><div class="crosshair"></div><b>${state.mode === 'guided' ? 'Follow the prompt' : 'Touch the drawing'}</b><p>Every visible part resolves back to a semantic object. Select one to inspect its relationships, anchors and hit region.</p></div>`}
          <div class="debug-controls">
            <label><input id="show-bounds" type="checkbox"${state.showBounds ? ' checked' : ''}/> hit bounds</label>
            <label><input id="show-anchors" type="checkbox"${state.showAnchors ? ' checked' : ''}/> anchors</label>
          </div>
          <div class="engine-contract">
            <span class="contract-kicker">${motion ? 'Motion compiler' : 'Invariant'}</span>
            <p>${motion
              ? `<code>${motion.entering}</code> enter · <code>${motion.moving}</code> move · <code>${motion.stable}</code> stable`
              : `Switching hands cannot change <code>${resolved.geometryFingerprint}</code>.`}</p>
          </div>
        </aside>
      </section>
    </main>`;

  bindEvents(program, checkpoint);
}

function renderCatalogue(): void {
  const started = performance.now();
  const entries: Array<{ label: string; question: string; spec: FigureSpec }> = [
    { label: 'Exploded slice', question: 'What parts describe this storage?', spec: sliceFixture(560, state.stress, 'pointer') },
    { label: 'Sectioned page', question: 'Where can the next record fit?', spec: pageFixture(560, state.stress, 'free-space') },
    { label: 'Isolation matrix', question: 'Which anomalies can still happen?', spec: isolationMatrixFixture(560, state.stress, 'matrix.cell.repeatable-read.phantom-read') },
    { label: 'Concurrency trace', question: 'What caused the request to resume?', spec: concurrencySequenceFixture(560, state.stress, state.stress ? 8 : 6, state.stress ? 'resume' : 'resume') },
    { label: 'Memory layout', question: 'Where did every byte go?', spec: memoryLayoutFixture(560, state.stress, state.stress ? 'memory.segment.cache-line-alignment-padding' : 'memory.segment.alignment-gap') },
    { label: 'Query plan', question: 'Where do rows enter, combine, and disappear?', spec: queryPlanFixture(560, state.stress, state.stress ? 'join-orders-customers' : 'join-customer') },
    { label: 'Latency distribution', question: 'What does the average hide in the tail?', spec: latencyDistributionFixture(560, state.stress, 'tail', true) },
    { label: 'Stack frames', question: 'What does one nested call add?', spec: stackFramesFixture(560, state.stress, 'call') },
    { label: 'Complexity argument', question: 'Why does the conclusion actually follow?', spec: complexityArgumentFixture(560, state.stress, state.stress ? 'bound-polynomial' : 'identify-dominant-term') },
    { label: 'Code runner', question: 'What code ran, and what did it actually produce?', spec: executionArtifactFixture(560, state.stress, state.stress ? stressExecutionArtifactFocus : defaultExecutionArtifactFocus, state.stress ? 'profiler' : 'editor', state.artifactTheme, true) },
    { label: 'Queue view', question: "Why doesn't a bigger buffer fix this?", spec: queueViewFixture(640, state.stress, 'head') },
    { label: 'B-tree lookup', question: 'Which key ranges can be discarded?', spec: btreeFixture(560, state.stress, state.stress ? 'leaf-21' : 'leaf-d') },
  ];
  const compiled = entries.map((entry) => ({ ...entry, figure: applyAnnotations(compileFigure(entry.spec)) }));
  const primitives = compilePrimitiveGallery(560);
  const workspaceWidth = window.innerWidth <= 780 ? 390 : 760;
  const workspaceSpec = executionArtifactFixture(workspaceWidth, false, defaultExecutionArtifactFocus, 'editor', state.artifactTheme, true);
  const evidenceRegisters = [{ spec: workspaceSpec, figure: compileFigure(workspaceSpec) }];
  const programs = [sliceAppendProgram(560), sliceReallocationProgram(560), stackCallProgram(560), btreeLookupProgram(560), queueOverloadProgram(560)].map(compileProgram);
  const programStates = programs.reduce((sum, program) => sum + program.states.length, 0);
  const invariantCount = programs.reduce((sum, program) => sum + program.invariants.length, 0);
  lastCompileMs = performance.now() - started;
  const totalShapes = compiled.reduce((sum, item) => sum + item.figure.shapes.length, 0);
  const totalAnchors = compiled.reduce((sum, item) => sum + item.figure.anchors.length, 0);

  root!.innerHTML = `<main class="foundation-shell">
    <header class="foundation-hero">
      <nav class="foundation-nav">
        <div class="eyebrow"><span class="live-dot"></span>Kiln Foundation Pack · ${PACK_VERSION}</div>
        <div class="foundation-actions">
          <a href="./hands.html">Drawing hands</a>
          <button data-mode="guided">Open guided lab</button>
          <button data-mode="explore">Inspect engine</button>
        </div>
      </nav>
      <div class="foundation-headline">
        <div>
          <h1>One visual language.<br/><span>Now it can teach change.</span></h1>
          <p>The complete ten-view vocabulary is now executable across eleven domain families—without letting AI invent a single coordinate.</p>
        </div>
        <div class="foundation-proof">
          <span>Compiled together, in this browser</span>
          <strong>${lastCompileMs.toFixed(2)} ms</strong>
          <div><b>${compiled.length}</b> families <b>${new Set(compiled.map((item) => item.figure.view)).size}</b> views <b>${compiled.length * 2}</b> fixtures <b>${totalShapes}</b> shapes <b>${totalAnchors}</b> anchors</div>
        </div>
      </div>
      <div class="compiler-flow" aria-label="Compiler pipeline">
        <span><b>01</b> semantic lesson data</span><i>→</i><span><b>02</b> view compiler</span><i>→</i><span><b>03</b> resolved scene graph</span><i>→</i><span><b>04</b> SVG now · Skia-ready</span>
      </div>
    </header>

    <section class="hand-lock panel">
      <div><span class="proof-label">Global visual hand</span><strong>${HANDS[state.hand].name}</strong><p>Switch it once. Every family redraws without changing a single coordinate or semantic relation.</p></div>
      <div class="hand-switch" data-control="catalogue-hand">
        ${Object.values(HANDS).map((hand) => `<button data-value="${hand.id}" class="${hand.id === state.hand ? 'active' : ''}"><span class="hand-swatch swatch-${hand.id}"></span><b>${hand.name}</b></button>`).join('')}
      </div>
      <div class="variation-control" data-control="catalogue-variation">
        <span class="proof-label">Fixture pressure</span>
        <div>${button('standard', 'Standard', !state.stress)}${button('stress', 'Stress data', state.stress)}</div>
        <div>${button('annotations-off', 'Clean', !state.annotations)}${button('annotations-on', 'Annotated', state.annotations)}</div>
        <code>${HAND_VERSION}</code>
      </div>
    </section>

    <a class="drawing-hands-entry" href="./hands.html">
      <span><b>New · drawing hands</b><small>See the same resolved figure change shape language, stroke hierarchy and semantic colour—without adding a background.</small></span>
      <strong>Open style lab →</strong>
    </a>

    <section class="evidence-register evidence-lesson">
      <header class="lesson-scene-copy"><div><span class="proof-label">Mechanism · execution evidence</span><h2>What did PostgreSQL actually do?</h2><p>The highlighted query is the lesson content. Its captured output arrives from the bottom of the same screen.</p></div></header>
      <div class="evidence-grid">
        ${evidenceRegisters.map(({ figure }) => `<div style="--stage-ratio:${figure.viewport.width / figure.viewport.height}">${renderSvg(figure, state.hand)}</div>`).join('')}
      </div>
    </section>

    <section class="runtime-contract panel">
      <div><span class="proof-label">Teaching runtime</span><h2>Change is now semantic data.</h2><p>Named states compile once; transitions carry verbs, correspondences, motion and truth assertions.</p></div>
      <div class="runtime-stats"><span><b>${programs.length}</b> programs</span><span><b>${programStates}</b> named states</span><span><b>${transitionVerbs.length}</b> verbs</span><span><b>${invariantCount}</b> semantic gates</span></div>
      <div class="program-fingerprints">${programs.map((program) => `<code>${program.id} · ${program.fingerprint}</code>`).join('')}</div>
    </section>

    ${lessonsSection()}

    <section class="foundation-grid">
      ${compiled.map(({ label, question, spec, figure }, index) => `<article class="foundation-card panel">
        <header>
          <div><span>${String(index + 1).padStart(2, '0')} · ${figure.view} view · ${state.stress ? 'stress' : 'standard'}</span><h2>${label}</h2><p>${question}</p></div>
          <code>${figure.geometryFingerprint}</code>
        </header>
        <div class="foundation-stage" style="--stage-ratio:${figure.viewport.width / figure.viewport.height}">
          ${renderSvg(figure, state.hand)}
        </div>
        <footer>
          <div><b>${figure.shapes.length}</b> shapes · <b>${figure.hitRegions.length}</b> touch targets · <b>${figure.relations.length}</b> relations</div>
          <button data-open-family="${figure.family}">Run lesson →</button>
        </footer>
        <details>
          <summary>Inspect coordinate-free source</summary>
          <div class="source-proof"><span>✓ no SVG</span><span>✓ no x/y positions</span><span>✓ no visual styles</span></div>
          <pre>${escapeHtml(JSON.stringify(spec, null, 2))}</pre>
        </details>
      </article>`).join('')}
    </section>

    <section class="foundation-grid">
      ${primitives.map(({ label, question, subject, spec, figure }, index) => `<article class="foundation-card panel">
        <header>
          <div><span>P${String(index + 1).padStart(2, '0')} · ${figure.view} view · ${subject}</span><h2>${label}</h2><p>${question}</p></div>
          <code>${figure.geometryFingerprint}</code>
        </header>
        <div class="foundation-stage" style="--stage-ratio:${figure.viewport.width / figure.viewport.height}">
          ${renderSvg(figure, state.hand)}
        </div>
        <footer>
          <div><b>${figure.shapes.length}</b> shapes · <b>${figure.hitRegions.length}</b> touch targets · <b>${figure.relations.length}</b> relations</div>
        </footer>
        <details>
          <summary>Inspect coordinate-free source</summary>
          <div class="source-proof"><span>✓ no SVG</span><span>✓ no x/y positions</span><span>✓ no visual styles</span></div>
          <pre>${escapeHtml(JSON.stringify(spec, null, 2))}</pre>
        </details>
      </article>`).join('')}
    </section>

    <section class="constitution panel">
      <div class="constitution-intro"><span class="proof-label">Locked constitution · ${FOUNDATION_VERSION}</span><h2>AI chooses meaning. The engine owns presentation.</h2><p>All ten base views are now executable. Lessons may provide entities, relations, quantities, state and provenance—not coordinates, colours, paths, easing or layout instructions.</p></div>
      <div class="token-stack">
        <span><i class="token-dot token-purple"></i>taught / active</span>
        <span><i class="token-dot token-blue"></i>reference / flow</span>
        <span><i class="token-dot token-amber"></i>caution / conditional</span>
        <span><i class="token-line"></i>one stroke hierarchy</span>
      </div>
      <div class="primitive-cloud">${primitiveKinds.map((item) => `<code>${item}</code>`).join('')}</div>
    </section>
  </main>`;

  root!.querySelectorAll<HTMLElement>('[data-mode]').forEach((element) => element.addEventListener('click', () => {
    state.mode = element.dataset.mode as ExperienceMode;
    resetInteraction();
    render();
  }));
  root!.querySelectorAll<HTMLElement>('[data-control="catalogue-hand"] [data-value]').forEach((element) => element.addEventListener('click', () => {
    state.hand = element.dataset.value as HandId;
    render();
  }));
  root!.querySelectorAll<HTMLElement>('[data-control="catalogue-variation"] [data-value]').forEach((element) => element.addEventListener('click', () => {
    if (element.dataset.value === 'stress' || element.dataset.value === 'standard') state.stress = element.dataset.value === 'stress';
    if (element.dataset.value === 'annotations-on' || element.dataset.value === 'annotations-off') state.annotations = element.dataset.value === 'annotations-on';
    render();
  }));
  root!.querySelectorAll<HTMLElement>('[data-open-lesson]').forEach((element) => element.addEventListener('click', () => {
    const scene = element.dataset.openScene ?? '1';
    window.location.search = `?experience=go-lessons&lesson=${element.dataset.openLesson}&scene=${scene}`;
  }));
  root!.querySelectorAll<HTMLElement>('[data-open-family]').forEach((element) => element.addEventListener('click', () => {
    state.family = element.dataset.openFamily as FigureSpec['family'];
    state.mode = 'guided';
    state.variant = 'primary';
    state.focus = 'overview';
    resetInteraction();
    render();
  }));
}

/**
 * The lessons section: three verified Go lessons, every scene compiled through
 * the family or primitive whose grammar matches that scene's assertion. The
 * thumbnails are the real scene figures at the phone width, not stand-ins.
 */
function lessonsSection(): string {
  const width = figureWidthFor(390);
  return `<section class="lesson-library">
    <header class="lesson-library-head">
      <div>
        <span class="proof-label">Verified Go lessons</span>
        <h2>Every number here came out of a real run.</h2>
        <p>Three lessons, ${GO_LESSONS.reduce((sum, lesson) => sum + lesson.scenes.length, 0)} scenes. Each scene names its capture, quotes it verbatim, and states the determinism that capture declared.</p>
      </div>
      <button data-open-lesson="${GO_LESSONS[0]!.id}" data-open-scene="1">Open the lessons →</button>
    </header>
    <div class="lesson-library-grid">
      ${GO_LESSONS.map((lesson) => {
        const opening = compileScene(lesson.scenes[0]!, width);
        return `<article class="lesson-library-card panel">
          <header><span>${lesson.module}</span><h3>${lesson.title}</h3><p>${lesson.premise}</p></header>
          <div class="lesson-library-stage" style="--stage-ratio:${opening.viewport.width / opening.viewport.height}">${renderSvg(opening, state.hand)}</div>
          <ol class="lesson-library-beats">${lesson.scenes.map((scene, index) => `<li><button data-open-lesson="${lesson.id}" data-open-scene="${index + 1}"><b>${scene.beat}</b><span>${escapeHtml(scene.heading)}</span></button></li>`).join('')}</ol>
        </article>`;
      }).join('')}
    </div>
  </section>`;
}

function focusOptionsFor(family: FigureSpec['family']): string[][] {
  if (family === 'slice-header') return [['overview', 'Whole figure'], ['pointer', 'Pointer relation'], ['extent', 'Len and cap']];
  if (family === 'database-page') return [['overview', 'Whole page'], ['free-space', 'Free-space'], ['tuple', 'Slot → tuple']];
  if (family === 'isolation-matrix') return [
    ['overview', 'Whole matrix'],
    ['matrix.cell.read-committed.dirty-read', 'Committed'],
    ['matrix.cell.repeatable-read.phantom-read', 'Engine rule'],
  ];
  if (family === 'concurrency-sequence') return [['overview', 'Full trace'], ['1', 'First event'], ['4', 'Through DB'], ['6', 'Complete']];
  if (family === 'memory-layout') return [['overview', 'Whole struct'], ['memory.segment.enabled', 'One-byte flag'], ['memory.segment.alignment-gap', 'Padding']];
  if (family === 'query-plan') return [['overview', 'Whole plan'], ['scan-orders', 'Source scan'], ['join-customer', 'Join'], ['sum-revenue', 'Result']];
  if (family === 'latency-distribution') return [['overview', 'Whole plot'], ['body', 'Body'], ['tail', 'Tail'], ['comparison', 'Compare']];
  if (family === 'stack-frames') return [['entry', 'Before call'], ['call', 'Push frame'], ['return', 'Return']];
  if (family === 'complexity-argument') return [['overview', 'Whole proof'], ['count-iterations', 'Count'], ['apply-series-identity', 'Identity'], ['identify-dominant-term', 'Conclusion']];
  if (family === 'execution-artifact') return [['overview', 'Whole artifact'], ['artifact.line.command', 'Command'], ['artifact.line.rows-removed', 'Evidence'], ['artifact.line.execution-time', 'Runtime']];
  if (family === 'btree') return [['overview', 'Whole tree'], ['root', 'Root'], ['branch-right', 'Branch'], ['leaf-d', 'Leaf']];
  if (family === 'protocol-state-machine') return [['overview', 'Whole contract'], ['protocol.transition.connect', 'Connect'], ['protocol.state.backoff', 'Backoff'], ['protocol.transition.retry', 'Retry']];
  return [['overview', 'Whole figure']];
}

function applyAnnotations(figure: ResolvedFigure): ResolvedFigure {
  if (!state.annotations) return figure;
  const layer = figure.family === 'memory-layout' && figure.hitRegions.some((hit) => hit.semanticId === 'memory.segment.alignment-gap')
    ? memoryTeachingAnnotations()
    : figure.family === 'execution-artifact' && figure.hitRegions.some((hit) => hit.semanticId === 'artifact.line.execution-time')
      ? executionEvidenceAnnotations()
      : undefined;
  if (!layer) return figure;
  const overlay = compileAnnotationLayer(layer, figure);
  return {
    ...figure,
    shapes: [...figure.shapes, ...overlay.shapes],
    anchors: [...figure.anchors, ...overlay.anchors],
    hitRegions: [...figure.hitRegions, ...overlay.hitRegions],
    relations: [...figure.relations, ...overlay.relations],
    activeSemanticIds: [...figure.activeSemanticIds, ...overlay.activeSemanticIds],
  };
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[character]!);
}

function lessonConsole(program: TransitionProgram, checkpoint: SemanticCheckpoint): string {
  const complete = state.step === program.checkpoints.length - 1;
  return `<section class="lesson-console panel" data-checkpoint="${checkpoint.id}" style="--checkpoint-count:${program.checkpoints.length}">
    <div class="lesson-status">
      <div><span>semantic transition</span><strong>${checkpoint.label}</strong></div>
      <small>${state.step + 1} / ${program.checkpoints.length}</small>
    </div>
    <div class="checkpoint-track" data-control="checkpoint">
      ${program.checkpoints.map((item, index) => `<button data-value="${index}" class="${index === state.step ? 'active' : ''}${index < state.step ? ' complete' : ''}" aria-label="Show checkpoint ${index + 1}: ${item.label}"><i>${index < state.step ? '✓' : index + 1}</i><span>${item.label}</span></button>`).join('')}
    </div>
    <p class="checkpoint-explanation">${checkpoint.explanation}</p>
    <div class="challenge ${state.feedback?.tone ?? ''}">
      <span>${complete ? 'Transition complete' : 'Your move'}</span>
      <strong>${complete ? program.outcome : checkpoint.prompt}</strong>
      ${state.feedback ? `<small>${state.feedback.message}</small>` : ''}
    </div>
    <div class="playback-controls">
      <button data-action="reset" ${state.step === 0 && !state.playing ? 'disabled' : ''}>↺ Replay</button>
      <button data-action="back" ${state.step === 0 ? 'disabled' : ''}>← Back</button>
      <button data-action="play" class="primary">${state.playing ? '■ Stop' : complete ? '▶ Play again' : '▶ Auto-play'}</button>
      <button data-action="next" ${complete ? 'disabled' : ''}>Next →</button>
    </div>
  </section>`;
}

function bindEvents(program: TransitionProgram, checkpoint: SemanticCheckpoint): void {
  root!.querySelectorAll<HTMLElement>('[data-control="mode"] [data-value]').forEach((element) => {
    element.addEventListener('click', () => {
      stopPlayback();
      state.mode = element.dataset.value as ExperienceMode;
      resetInteraction();
      render();
    });
  });
  root!.querySelectorAll<HTMLElement>('[data-control="family"] [data-value]').forEach((element) => {
    element.addEventListener('click', () => {
      stopPlayback();
      state.family = element.dataset.value as AppState['family'];
      state.variant = 'primary';
      state.focus = 'overview';
      resetInteraction();
      render();
    });
  });
  root!.querySelectorAll<HTMLElement>('[data-control="variant"] [data-value]').forEach((element) => {
    element.addEventListener('click', () => {
      stopPlayback();
      state.variant = element.dataset.value as TransitionVariant;
      resetInteraction();
      render();
    });
  });
  root!.querySelectorAll<HTMLElement>('[data-control="hand"] [data-value]').forEach((element) => {
    element.addEventListener('click', () => {
      state.hand = element.dataset.value as HandId;
      render();
    });
  });
  root!.querySelectorAll<HTMLElement>('[data-control="focus"] [data-value]').forEach((element) => {
    element.addEventListener('click', () => {
      state.focus = element.dataset.value ?? 'overview';
      render();
    });
  });
  root!.querySelectorAll<HTMLElement>('[data-control="checkpoint"] [data-value]').forEach((element) => {
    element.addEventListener('click', () => goToStep(Number(element.dataset.value)));
  });
  root!.querySelector<HTMLInputElement>('#width')?.addEventListener('input', (event) => {
    stopPlayback();
    state.width = Number((event.currentTarget as HTMLInputElement).value);
    resetInteraction();
    render();
  });
  root!.querySelector<HTMLInputElement>('#stress')?.addEventListener('change', (event) => {
    state.stress = (event.currentTarget as HTMLInputElement).checked;
    state.selected = undefined;
    render();
  });
  root!.querySelector<HTMLInputElement>('#show-bounds')?.addEventListener('change', (event) => {
    state.showBounds = (event.currentTarget as HTMLInputElement).checked;
    render();
  });
  root!.querySelector<HTMLInputElement>('#show-anchors')?.addEventListener('change', (event) => {
    state.showAnchors = (event.currentTarget as HTMLInputElement).checked;
    render();
  });
  root!.querySelector<HTMLElement>('[data-action="reset"]')?.addEventListener('click', () => goToStep(0));
  root!.querySelector<HTMLElement>('[data-action="back"]')?.addEventListener('click', () => goToStep(Math.max(0, state.step - 1)));
  root!.querySelector<HTMLElement>('[data-action="next"]')?.addEventListener('click', () => goToStep(Math.min(program.checkpoints.length - 1, state.step + 1)));
  root!.querySelector<HTMLElement>('[data-action="play"]')?.addEventListener('click', () => state.playing ? stopAndRender() : startPlayback(program));
  root!.querySelectorAll<SVGElement>('.hit-region').forEach((element) => {
    const select = (): void => selectSemanticObject(element.dataset.semanticId, checkpoint, program);
    element.addEventListener('click', select);
    element.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter' || (event as KeyboardEvent).key === ' ') select();
    });
  });
}

function selectSemanticObject(semanticId: string | undefined, checkpoint: SemanticCheckpoint, program: TransitionProgram): void {
  if (!semanticId) return;
  stopPlayback();
  state.selected = semanticId;
  if (state.mode !== 'guided' || !checkpoint.expectedSemanticId) {
    state.feedback = undefined;
    render();
    return;
  }
  if (semanticId !== checkpoint.expectedSemanticId) {
    state.feedback = { tone: 'wrong', message: 'That object is real, but it does not perform this transition step. Try the prompt again.' };
    render();
    return;
  }
  state.feedback = { tone: 'correct', message: `Correct: ${semanticId}` };
  if (state.step < program.checkpoints.length - 1) {
    pendingMotionFrom = resolved;
    state.step += 1;
  }
  render();
}

function goToStep(step: number): void {
  stopPlayback();
  pendingMotionFrom = resolved;
  state.step = step;
  state.selected = undefined;
  state.feedback = undefined;
  render();
}

function startPlayback(program: TransitionProgram): void {
  stopPlayback();
  state.playing = true;
  if (state.step === program.checkpoints.length - 1) {
    pendingMotionFrom = resolved;
    state.step = 0;
  }
  state.selected = undefined;
  state.feedback = undefined;
  render();
  scheduleNext(program);
}

function scheduleNext(program: TransitionProgram): void {
  playTimer = window.setTimeout(() => {
    if (!state.playing) return;
    if (state.step >= program.checkpoints.length - 1) {
      state.playing = false;
      render();
      return;
    }
    pendingMotionFrom = resolved;
    state.step += 1;
    render();
    scheduleNext(program);
  }, 1050);
}

function stopPlayback(): void {
  state.playing = false;
  if (playTimer !== undefined) window.clearTimeout(playTimer);
  playTimer = undefined;
}

function stopAndRender(): void {
  stopPlayback();
  render();
}

function resetInteraction(): void {
  state.step = 0;
  state.selected = undefined;
  state.feedback = undefined;
  pendingMotionFrom = undefined;
}

function describeSelection(figure: ResolvedFigure, semanticId: string): {
  semanticId: string;
  shapes: string[];
  anchors: string[];
  relations: ResolvedFigure['relations'];
  hit: ResolvedFigure['hitRegions'][number] | undefined;
} {
  return {
    semanticId,
    shapes: figure.shapes.filter((shape) => shape.semanticId === semanticId).map((shape) => `${shape.kind} · ${shape.id}`),
    anchors: figure.anchors.filter((anchor) => anchor.semanticId === semanticId).map((anchor) => `${anchor.id} (${anchor.point.x}, ${anchor.point.y})`),
    relations: figure.relations.filter((relation) => relation.from === semanticId || relation.to === semanticId),
    hit: figure.hitRegions.find((hit) => hit.semanticId === semanticId),
  };
}

function selectionMarkup(selection: ReturnType<typeof describeSelection>): string {
  return `<div class="selection">
    <div class="selection-id"><span>semantic id</span><strong>${selection.semanticId}</strong></div>
    <dl>
      <dt>Rendered parts</dt><dd>${selection.shapes.length ? selection.shapes.map((item) => `<code>${item}</code>`).join('') : '<em>relation-only object</em>'}</dd>
      <dt>Anchors</dt><dd>${selection.anchors.length ? selection.anchors.map((item) => `<code>${item}</code>`).join('') : '<em>none</em>'}</dd>
      <dt>Hit region</dt><dd>${selection.hit ? `<code>${selection.hit.box.width} × ${selection.hit.box.height} at ${selection.hit.box.x}, ${selection.hit.box.y}</code>` : '<em>none</em>'}</dd>
      <dt>Relations</dt><dd>${selection.relations.length ? selection.relations.map((relation) => `<div class="relation"><b>${relation.type}</b><span>${relation.from} → ${relation.to}</span><small>${relation.label}</small></div>`).join('') : '<em>none</em>'}</dd>
    </dl>
  </div>`;
}

function button(value: string, label: string, active: boolean): string {
  return `<button data-value="${value}" class="${active ? 'active' : ''}">${label}</button>`;
}

if (new URLSearchParams(window.location.search).get('experience') === 'go-lessons') renderGoLessons(root);
else render();
